import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import { authClient } from './auth.js';
import {
  getCachedUser,
  setCachedUser,
  subscribeCachedUser,
  type SessionUser,
} from './cached-session.js';
import { closeLocalDatabase, openLocalDatabase, type LocalDatabase } from './db/powersync.js';

/**
 * Session + local database of the signed-in user (docs/07 §5, §7). The database opens when a
 * session is known and is cleared and closed on sign-out, so a reload after signing out shows
 * no data.
 *
 * Local-first: the identity of the last session is remembered in this browser so the app opens
 * its local database when the API cannot be reached (the session cookie is still there; the
 * server decides again as soon as it answers). An explicit «no session» from the server clears it.
 */

export type { SessionUser } from './cached-session.js';

export interface SessionState {
  status: 'loading' | 'anonymous' | 'opening' | 'ready';
  user: SessionUser | null;
  /** True when the user comes from the local cache because the API did not answer. */
  offline: boolean;
  local: LocalDatabase | null;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const { data: server, isPending, error } = authClient.useSession();
  const cached = useSyncExternalStore(subscribeCachedUser, getCachedUser);
  const [local, setLocal] = useState<LocalDatabase | null>(null);
  const closing = useRef<LocalDatabase | null>(null);

  // Resolve the user: the server's answer wins; a network failure falls back to the cache.
  const serverUser = useMemo<SessionUser | null>(
    () =>
      server ? { id: server.user.id, email: server.user.email, name: server.user.name } : null,
    [server],
  );
  const networkFailed = !isPending && !server && error !== null;
  const user = serverUser ?? (networkFailed ? cached : null);
  const userId = user?.id ?? null;

  // Remember the identity the server confirmed; forget it when the server says there is no session.
  useEffect(() => {
    if (isPending) return;
    if (serverUser) setCachedUser(serverUser);
    else if (!networkFailed) setCachedUser(null);
  }, [isPending, serverUser, networkFailed]);

  useEffect(() => {
    if (!userId || local?.userId === userId) return;
    let cancelled = false;
    openLocalDatabase(userId)
      .then(async (opened) => {
        if (cancelled) {
          await opened.db.close();
          return;
        }
        setLocal(opened);
      })
      .catch((error: unknown) => {
        console.error('[flow] no se ha podido abrir la base local', error);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, local?.userId]);

  // Session gone (expired or signed out elsewhere): stop syncing and clear local data.
  useEffect(() => {
    if (isPending || userId || !local || closing.current === local) return;
    closing.current = local;
    void closeLocalDatabase(local).finally(() => {
      closing.current = null;
      setLocal((current) => (current === local ? null : current));
    });
  }, [isPending, userId, local]);

  const signOut = useCallback(async () => {
    const current = local;
    setLocal(null);
    setCachedUser(null);
    if (current) await closeLocalDatabase(current);
    await authClient.signOut();
  }, [local]);

  const value = useMemo<SessionState>(() => {
    const status: SessionState['status'] = isPending
      ? 'loading'
      : !user
        ? 'anonymous'
        : local?.userId === user.id
          ? 'ready'
          : 'opening';
    return { status, user, offline: !serverUser && user !== null, local, signOut };
  }, [isPending, user, serverUser, local, signOut]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside SessionProvider');
  return value;
}
